'use client';

import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { regionalizePath } from '@/lib/config/branches-config';

type PortfolioItem = Record<string, any>;

type PortfolioFilter = {
  code: string;
  name: string;
  count: number;
};

type PortfolioInfiniteGridProps = {
  initialItems: PortfolioItem[];
  totalPages: number;
  limit: number;
  initialPage?: number;
  geoCode?: string;
  filters?: PortfolioFilter[];
  initialFilter?: string;
};

function getProjectKey(project: PortfolioItem) {
  return project?.ID || project?.id || project?.slug || project?.CODE || project?.code || project?.name || project?.NAME || '';
}

function getProjectTitle(project: PortfolioItem) {
  return project?.NAME || project?.title || project?.name || '';
}

function getProjectImage(project: PortfolioItem) {
  return project?.imageUrl || project?.PREVIEW_PICTURE || project?.PREVIEW_PICTURE_URL || project?.image || '';
}

function getProjectSlug(project: PortfolioItem, fallback: string) {
  return project?.slug || project?.CODE || project?.code || fallback;
}

export default function PortfolioInfiniteGrid({
  initialItems,
  totalPages,
  limit,
  initialPage = 1,
  geoCode,
  filters = [],
  initialFilter = '',
}: PortfolioInfiniteGridProps) {
  const [items, setItems] = useState(initialItems || []);
  const [page, setPage] = useState(initialPage || 1);
  const [pagesTotal, setPagesTotal] = useState(totalPages || 1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const requestSerialRef = useRef(0);

  const hasMore = page < pagesTotal;

  const buildHref = useCallback(
    (slug: string) => {
      const path = `/portfolio/${slug}`;
      return geoCode ? regionalizePath(path, geoCode) : path;
    },
    [geoCode]
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    setIsLoading(true);
    setError('');

    const nextPage = page + 1;

    try {
      const filterParam = activeFilter ? `&filter=${encodeURIComponent(activeFilter)}` : '';
      const response = await fetch(`/api/portfolio?page=${nextPage}&limit=${limit}${filterParam}`);
      if (!response.ok) {
        throw new Error(`Failed to load portfolio page ${nextPage}`);
      }

      const data = await response.json();
      const newItems: PortfolioItem[] = Array.isArray(data?.items) ? data.items : [];

      setItems((prev: PortfolioItem[]) => {
        const existing = new Set(prev.map((project: PortfolioItem) => getProjectKey(project)));
        const uniqueNewItems = newItems.filter((project: PortfolioItem) => {
          const key = getProjectKey(project);
          if (!key) return true;
          if (existing.has(key)) return false;
          existing.add(key);
          return true;
        });

        return [...prev, ...uniqueNewItems];
      });

      setPage(data?.currentPage || nextPage);
      setPagesTotal(data?.totalPages || pagesTotal);
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить проекты. Попробуйте еще раз чуть позже.');
    } finally {
      setIsLoading(false);
    }
  }, [activeFilter, hasMore, isLoading, limit, page, pagesTotal]);

  const selectFilter = useCallback(async (filterCode: string) => {
    if (filterCode === activeFilter || isLoading) return;

    const requestId = ++requestSerialRef.current;
    setIsLoading(true);
    setError('');

    try {
      const filterParam = filterCode ? `&filter=${encodeURIComponent(filterCode)}` : '';
      const response = await fetch(`/api/portfolio?page=1&limit=${limit}${filterParam}`);
      if (!response.ok) {
        throw new Error(`Failed to filter portfolio by ${filterCode || 'all'}`);
      }

      const data = await response.json();
      if (requestId !== requestSerialRef.current) return;

      setItems(Array.isArray(data?.items) ? data.items : []);
      setPage(data?.currentPage || 1);
      setPagesTotal(data?.totalPages || 1);
      setActiveFilter(data?.activeFilter || filterCode);
    } catch (err) {
      console.error(err);
      if (requestId === requestSerialRef.current) {
        setError('Не удалось применить фильтр. Попробуйте еще раз чуть позже.');
      }
    } finally {
      if (requestId === requestSerialRef.current) {
        setIsLoading(false);
      }
    }
  }, [activeFilter, isLoading, limit]);

  useEffect(() => {
    if (!hasMore) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '260px 0px' }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMore]);

  return (
    <div>
      {filters.length > 0 && (
        <div className="mb-7" aria-label="Фильтр проектов по направлению">
          <div className="mb-3 text-sm font-medium text-gray-700">Направление проекта</div>
          <div className="flex gap-2 overflow-x-auto pb-2" role="group">
            <button
              type="button"
              onClick={() => selectFilter('')}
              disabled={isLoading}
              aria-pressed={activeFilter === ''}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition disabled:cursor-wait disabled:opacity-60 ${
                activeFilter === ''
                  ? 'border-blue-950 bg-blue-950 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-blue-950 hover:text-blue-950'
              }`}
            >
              Все проекты
            </button>
            {filters.map((filter) => (
              <button
                key={filter.code}
                type="button"
                onClick={() => selectFilter(filter.code)}
                disabled={isLoading}
                aria-pressed={activeFilter === filter.code}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition disabled:cursor-wait disabled:opacity-60 ${
                  activeFilter === filter.code
                    ? 'border-blue-950 bg-blue-950 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-blue-950 hover:text-blue-950'
                }`}
              >
                {filter.name} <span className="opacity-70">{filter.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-10">
        {items.map((project, index) => {
          const projectId = getProjectKey(project) || `project-${index}`;
          const projectTitle = getProjectTitle(project);
          const projectImage = getProjectImage(project);
          const projectSlug = getProjectSlug(project, projectId);

          return (
            <a key={`project-${projectId}-${index}`} href={buildHref(projectSlug)} className="block h-full">
              <article className="flex h-full flex-col items-start justify-between group">
                <div className="relative w-full">
                  {projectImage ? (
                    <Image
                      alt={projectTitle}
                      src={projectImage}
                      width={800}
                      height={600}
                      className="aspect-video w-full rounded-2xl bg-gray-100 object-cover sm:aspect-2/1 lg:aspect-3/2"
                      sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                      quality={75}
                    />
                  ) : (
                    <div className="aspect-video w-full rounded-2xl bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">Нет изображения</span>
                    </div>
                  )}
                  <div className="absolute inset-0 rounded-2xl inset-ring inset-ring-gray-900/10" />
                </div>
                <div className="flex max-w-xl grow flex-col justify-between w-full">
                  <div className="relative grow w-full">
                    <h3 className="mt-2 md:mt-3 text-sm md:text-base lg:text-base font-semibold text-gray-900 group-hover:text-gray-600">
                      {projectTitle}
                    </h3>
                  </div>
                </div>
              </article>
            </a>
          );
        })}
      </div>

      {!isLoading && items.length === 0 && (
        <div className="mb-8 rounded-xl bg-gray-50 px-4 py-10 text-center text-gray-600">
          В этом направлении пока нет опубликованных проектов.
        </div>
      )}

      {error && (
        <div className="text-center text-sm text-red-600 mb-3" role="alert">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center mb-6">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-900 border-t-transparent" aria-label="Загрузка" />
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div className="text-center text-sm text-gray-500 mb-6">Вы просмотрели все проекты</div>
      )}

      {hasMore && <div ref={loadMoreRef} className="h-10 w-full" />}
    </div>
  );
}
